import {
  Button,
  ButtonGroup,
  DestructiveConfirmDialog,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  Input,
  useStateWithSetAction,
  VirtualizedDataTable,
  type VirtualizedDataTableProps,
} from "@tellescope/ui";
import { Select } from "@tellescope/design-system";
import { TellescopeModelFromName } from "@tellescope/tanstack-query";
import { SetStateAction, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Filter, Plus, Trash2 } from "lucide-react";
import { useForm } from "@tellescope/forms";
import { useAppPermissionQuery } from "../../../permissions";
import { useTeamMembersTable } from "../../../hooks/useTeamMembersTable";
import { useTeamDefaultViewShallowStore } from "../../../store";
import { TeamMembersTableConfigDialog } from "../../../components/team/TeamMembersTableConfigDialog";
import { EditUserProfileDialogContents } from "./EditUserProfileDialogContents";
import { InviteUserDialogContents } from "./InviteUserDialogContents";

type TeamMember = TellescopeModelFromName<"users">;

const VIEWS_PATH = "/settings/team/views";

export const TeamMembersTable = () => {
  const { t } = useTranslation("settings");
  const navigate = useNavigate();
  const params = useParams();
  const [search, setSearch] = useSearchParams();
  const viewId = params.viewId;
  const { lastSelectedViewId, setLastSelectedViewId } = useTeamDefaultViewShallowStore(
    "lastSelectedViewId",
    "setLastSelectedViewId"
  );

  // useEffect is justified here because this store is a localStorage store that we want to sync to
  useEffect(() => {
    if (viewId && viewId !== lastSelectedViewId) {
      setLastSelectedViewId(viewId);
    }
  }, [viewId, lastSelectedViewId, setLastSelectedViewId]);

  const setViewId = useCallback(
    (idOrUpdater: SetStateAction<string>) => {
      const newId = typeof idOrUpdater === "function" ? idOrUpdater(viewId ?? "all") : idOrUpdater;
      navigate(`${VIEWS_PATH}/${newId}`);
    },
    [navigate, viewId]
  );

  const teamMembersTable = useTeamMembersTable({
    viewId,
    setViewId,
    onSelectUser: (userId) => {
      setSearch((prev) => {
        prev.append("userId", userId);
        return prev;
      });
    },
  });

  const [isAddViewDialogOpen, setIsAddViewDialogOpen] = useStateWithSetAction(false, (isOpen) => {
    if (!isOpen) {
      addViewForm.resetForm();
    }
  });
  const [viewConfigDialogIsOpen, setViewConfigDialogIsOpen] = useState(false);
  const [deleteDialogIsOpen, setDeleteDialogIsOpen] = useState(false);

  const [inviteUserIsOpen, setInviteUserIsOpen] = useState(false);

  const addViewForm = useForm({
    fields: {
      title: { type: "text", required: true, initialValue: "" },
    },
    validateOn: "blur",
    onSubmit: (values) => {
      if (!values.title) return;

      const saveAsFilter = teamMembersTable.selectedView.filter;

      teamMembersTable.createTableViewMutation.mutateAsync(
        {
          page: `${teamMembersTable.TELLESCOPE_2_TABLE_VIEW_PREFIX}TeamMembers`,
          title: values.title,
          columns: teamMembersTable.selectedView.columns,
          ...(saveAsFilter !== undefined ? { filter: saveAsFilter } : undefined),
        },
        {
          onSuccess: (res) => {
            teamMembersTable.resetDefaultView();
            navigate(`${VIEWS_PATH}/${res.id}`);
          },
        }
      );
      setIsAddViewDialogOpen(false);
    },
  });

  const {
    delete: { access: canDelete },
  } = useAppPermissionQuery({ model: "table_views", actions: ["delete"] });

  const activeFiltersCount = teamMembersTable.activeFiltersCount;

  const openUserId = search.get("userId") ?? undefined;

  if (!viewId) {
    return <Navigate replace to="./all" />;
  }

  const actionButtons: NonNullable<VirtualizedDataTableProps<TeamMember>["actionButtons"]> = [
    <ButtonGroup key="view-management">
      <Select value={teamMembersTable.viewId} onValueChange={(value) => navigate(`${VIEWS_PATH}/${value}`)}>
        <Select.Trigger
          data-trackingid="team-members-table-view-selector"
          className="w-auto min-w-[120px] max-w-[200px]"
        >
          <Select.Value placeholder={t("All")} />
        </Select.Trigger>
        <Select.Content>
          <Select.Item value="all">{t("All")}</Select.Item>
          {teamMembersTable.views.map((view) => (
            <Select.Item key={view.id} value={view.id}>
              {view.title}
            </Select.Item>
          ))}
        </Select.Content>
      </Select>
      <Button
        variant="outline"
        className="relative h-8 w-8 p-0 border-l-0"
        onClick={() => setViewConfigDialogIsOpen(true)}
        hoverLabel={t("Configure View")}
        data-trackingid="team-members-table-configure-view"
      >
        <Filter strokeWidth={1.8} />
        {!!activeFiltersCount && <span className="absolute top-1 right-1 h-2 w-2 rounded-full bg-primary" />}
      </Button>
      {teamMembersTable.viewId !== "all" && canDelete && (
        <Button
          variant="outline"
          className="h-8 w-8 p-0 border-l-0"
          onClick={() => setDeleteDialogIsOpen(true)}
          hoverLabel={t("Delete View")}
          data-trackingid="team-members-table-delete-view"
        >
          <Trash2 strokeWidth={1.8} className="text-destructive" />
        </Button>
      )}
    </ButtonGroup>,
    ...(teamMembersTable.isDefaultViewModified
      ? [
          <Button
            key="save-as-view"
            variant="outline"
            size="sm"
            className="font-light text-xs"
            onClick={() => setIsAddViewDialogOpen(true)}
            data-trackingid="team-members-table-save-default-as-view"
          >
            {t("Save as View")}
          </Button>,
        ]
      : []),
    <Button key="invite-team-member" className="h-8" onClick={() => setInviteUserIsOpen(true)}>
      <Plus />
      {t("Invite User")}
    </Button>,
  ];

  return (
    <div className="flex flex-col flex-1 min-h-0 px-6 pt-4">
      <VirtualizedDataTable
        {...teamMembersTable.virtualizedTableProps}
        viewportHeight={{ offset: 320 }}
        actionButtons={actionButtons}
        searchPlaceholder={t("Search team members...")}
        hideScrollbar
      />

      <Dialog open={isAddViewDialogOpen} onOpenChange={setIsAddViewDialogOpen}>
        <DialogContent>
          <div className="space-y-1">
            <DialogTitle className="font-medium text-sm">{t("Save as View")}</DialogTitle>
            <DialogDescription className="text-xs">
              {t("Enter a name for the new view. It will be saved with your current column configuration.")}
            </DialogDescription>
          </div>
          <form {...addViewForm.getFormProps()} className="flex flex-col gap-2">
            <Input
              placeholder={t("View name...")}
              {...addViewForm.getFieldProps("title")}
              onKeyDownCapture={(e) => {
                if (e.key === "Enter" && addViewForm.values.title) {
                  e.stopPropagation();
                  addViewForm.submitForm();
                }
              }}
              autoFocus
            />
            <div className="flex justify-end gap-2">
              <Button onClick={() => setIsAddViewDialogOpen(false)} variant="outline">
                {t("Cancel")}
              </Button>
              <Button
                type="submit"
                disabled={!addViewForm.isValid}
                data-trackingid="team-members-table-create-table-view-dialog-confirm"
              >
                {t("Save View")}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {!teamMembersTable.isLoadingViews && (
        <TeamMembersTableConfigDialog
          title={t("Configure {{tableViewName}}", {
            tableViewName: teamMembersTable.selectedView.title || t("All"),
          })}
          open={viewConfigDialogIsOpen}
          onOpenChange={(open) => setViewConfigDialogIsOpen(open)}
          columnConfigFormProps={{
            combinedFieldToColumnConfigMap: teamMembersTable.combinedFieldToColumnConfigMap,
            columnVisibility: teamMembersTable.columnVisibility,
            orderedVisibleColumnIds: teamMembersTable.orderedVisibleColumnIds,
          }}
          onApplyConfig={teamMembersTable.onApplyConfig}
          activeFilters={teamMembersTable.activeFilters}
          tagOptions={teamMembersTable.tagOptions}
          roleOptions={teamMembersTable.roleOptions}
          onClearAll={
            teamMembersTable.isDefaultViewModified
              ? () => {
                  teamMembersTable.resetDefaultView();
                  setViewConfigDialogIsOpen(false);
                }
              : undefined
          }
          isDirty={teamMembersTable.isDefaultViewModified}
        />
      )}

      <DestructiveConfirmDialog
        onConfirm={() => {
          teamMembersTable.deleteTableViewMutation.mutateAsync(teamMembersTable.viewId);
          navigate(`${VIEWS_PATH}/all`);
        }}
        loading={teamMembersTable.deleteTableViewMutation.isPending}
        open={deleteDialogIsOpen}
        onOpenChange={(open) => setDeleteDialogIsOpen(open)}
        description={t("This will permanently delete the view. This action cannot be undone.")}
      />
      <Dialog
        open={!!openUserId}
        onOpenChange={(open) => {
          if (!open) {
            setSearch((prev) => {
              prev.delete("userId");
              return prev;
            });
          }
        }}
      >
        <EditUserProfileDialogContents
          userId={openUserId}
          onSuccess={() => {
            setSearch((prev) => {
              prev.delete("userId");
              return prev;
            });
          }}
        />
      </Dialog>

      <Dialog open={inviteUserIsOpen} onOpenChange={(open) => setInviteUserIsOpen(open)}>
        <InviteUserDialogContents
          roleOptions={teamMembersTable.roleOptions}
          tagOptions={teamMembersTable.tagOptions}
          onSuccess={() => setInviteUserIsOpen(false)}
        />
      </Dialog>
    </div>
  );
};
